import type { MessageAttachmentDto, MessageDto } from "@multi-chat/shared";
import { isImageAttachment, isVideoAttachment, usesStickerPresentation } from "../attachment-kind.js";
import { messageDisplayTimestampIso, shouldShowConversationDateSeparator } from "../conversation-date.js";
import { isOutboundMessage } from "../inbox-display.js";

function isPhotoImageAttachment(attachment: MessageAttachmentDto): boolean {
  return isImageAttachment(attachment) && !usesStickerPresentation(attachment);
}

/** True when this message starts a new conversational turn vs the previous row. */
export function isMessageTurnChange(
  previous: MessageDto | undefined,
  current: MessageDto,
): boolean {
  if (previous == null) return false;
  return isOutboundMessage(previous) !== isOutboundMessage(current);
}

/** Longest pause that still reads as one burst of same-sender bubbles. */
export const MESSAGE_RUN_MAX_GAP_MS = 5 * 60 * 1000;

/**
 * True when `current` visually continues `previous`'s bubble run: same direction
 * and sender, same calendar day (no date pill between), and a short pause.
 * Presentation only — the messages themselves stay separate records.
 */
export function continuesMessageRun(
  previous: MessageDto | undefined,
  current: MessageDto | undefined,
): boolean {
  if (previous == null || current == null) return false;
  if (isMessageTurnChange(previous, current)) return false;
  // Private-reply origin cards break the visual run so the banner stays attached.
  if (previous.originatingSocialComment || current.originatingSocialComment) return false;
  if (previous.organizationMemberId !== current.organizationMemberId) return false;
  if (previous.origin !== current.origin) return false;
  if (shouldShowConversationDateSeparator(previous, current)) return false;
  const gapMs =
    Date.parse(messageDisplayTimestampIso(current)) - Date.parse(messageDisplayTimestampIso(previous));
  return Number.isFinite(gapMs) && Math.abs(gapMs) <= MESSAGE_RUN_MAX_GAP_MS;
}

export function messageHasImageAttachments(message: MessageDto): boolean {
  return (message.attachments ?? []).some(isPhotoImageAttachment);
}

function allAttachmentsMatch(
  message: MessageDto,
  match: (attachment: MessageAttachmentDto) => boolean,
): boolean {
  const attachments = message.attachments ?? [];
  return attachments.length > 0 && attachments.every(match);
}

/** Image message with no separate text body — media is the bubble. Stickers are not images. */
export function isImageMediaOnlyMessage(message: MessageDto): boolean {
  if (message.templatePresentation) {
    return false;
  }
  if (message.messageType === "INTERACTIVE") {
    return false;
  }
  if (!messageHasImageAttachments(message)) {
    return false;
  }
  if (!allAttachmentsMatch(message, isPhotoImageAttachment)) {
    return false;
  }
  return !message.content?.trim();
}

/** Image message with caption text below the media in a compact bubble. */
export function isCaptionedImageMessage(message: MessageDto): boolean {
  if (message.templatePresentation) {
    return false;
  }
  if (!messageHasImageAttachments(message)) {
    return false;
  }
  if (!message.content?.trim()) {
    return false;
  }
  return allAttachmentsMatch(message, isPhotoImageAttachment);
}

export function isVideoMediaOnlyMessage(message: MessageDto): boolean {
  if (message.templatePresentation || message.messageType === "INTERACTIVE") {
    return false;
  }
  if (!allAttachmentsMatch(message, isVideoAttachment)) {
    return false;
  }
  return !message.content?.trim();
}

export function isCaptionedVideoMessage(message: MessageDto): boolean {
  if (message.templatePresentation) {
    return false;
  }
  if (!message.content?.trim()) {
    return false;
  }
  return allAttachmentsMatch(message, isVideoAttachment);
}

export function isStickerOnlyMessage(message: MessageDto): boolean {
  if (message.templatePresentation || message.messageType === "INTERACTIVE") {
    return false;
  }
  if (!allAttachmentsMatch(message, usesStickerPresentation)) {
    return false;
  }
  return !message.content?.trim();
}

export function usesCompactImageBubble(message: MessageDto): boolean {
  return isImageMediaOnlyMessage(message) || isCaptionedImageMessage(message);
}

export function usesOverlayMediaBubble(message: MessageDto): boolean {
  return isImageMediaOnlyMessage(message) || isVideoMediaOnlyMessage(message);
}

export function usesCaptionedMediaBubble(message: MessageDto): boolean {
  return isCaptionedImageMessage(message) || isCaptionedVideoMessage(message);
}
